/**
 * One shared source of truth for money display — ported from legacy
 * webledger's `fmtAmount`/`fmtNum` (`assets/app.js`), which every page there
 * called instead of reimplementing formatting per view. Decimal places come
 * from the currency's own `fraction` (an admin-configurable 0-4, default 2)
 * — never a hardcoded constant. See docs/legacy/webledger.ux-audit-shared.md.
 *
 * Storage precision (always 4dp, for exchange-rate fidelity) is a separate,
 * unrelated concern — do not conflate the two. This module is display-only.
 */

export interface MoneyCurrency {
  fraction: number;
  symbol?: string;
  code: string;
}

const FALLBACK: MoneyCurrency = { fraction: 2, code: '' };

/** Formatted magnitude only, no symbol/code prefix — for use inside a chip that shows the code separately. */
export function formatNum(value: string | number, currency?: MoneyCurrency): string {
  const c = currency ?? FALLBACK;
  const n = Number(value) || 0;
  const s = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: c.fraction, maximumFractionDigits: c.fraction,
  });
  return n < 0 ? `-${s}` : s;
}

/** Full display form: sign, symbol-or-code prefix, currency-aware decimals. Never used inside a live editable input — display only. */
export function formatAmount(value: string | number, currency?: MoneyCurrency): string {
  const c = currency ?? FALLBACK;
  const n = Number(value) || 0;
  const s = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: c.fraction, maximumFractionDigits: c.fraction,
  });
  const prefix = c.symbol || (c.code ? `${c.code} ` : '');
  const t = prefix + s;
  return n < 0 ? `-${t}` : t;
}

/**
 * The `step` a money input should carry, for a currency with this many
 * decimals.
 *
 * `0.01` for two, `1` for a currency that has none — so the browser's own
 * validation, and its up/down keys, move by the smallest amount the currency
 * actually has rather than by a whole unit of it. Without this a two-decimal
 * amount is reported invalid the moment somebody types the cents.
 *
 * Here rather than in one form, because a money input is a money input: the
 * split table, the short form and anything else that asks for an amount want
 * the same control, and a second copy of this is a second place for a
 * zero-decimal currency to be got wrong.
 */
export function stepForFraction(fraction: number): string {
  return fraction > 0 ? (1 / 10 ** fraction).toFixed(fraction) : '1';
}
